/**
 * CR402: Registry Table Panel
 * 
 * Advanced table view for bulk registry edits.
 * Moved from DealCardsTab to Deal Types tab per CR402.
 */

import { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { CreditCard, Save, RefreshCw, FileImage } from "lucide-react";
import type { DealTypeRegistryEntry } from "@/providers/DealCardConfigProvider";

// ================================================================
// TYPES
// ================================================================

export interface RegistryTablePanelProps {
  registry: DealTypeRegistryEntry[];
  loading: boolean;
  onRefresh: () => void;
}

// CR339: Removed RIBBON_THEMES - theme colors are now managed in Theme Labs

// ================================================================
// COMPONENT
// ================================================================

export function RegistryTablePanel({
  registry,
  loading,
  onRefresh,
}: RegistryTablePanelProps) {
  // Track edits per row
  const [registryEdits, setRegistryEdits] = useState<Record<string, Partial<DealTypeRegistryEntry>>>({});
  const [savingRegistry, setSavingRegistry] = useState<string | null>(null);

  // Get current registry value (edited or original)
  const getRegistryValue = <K extends keyof DealTypeRegistryEntry>(
    key: string,
    field: K
  ): DealTypeRegistryEntry[K] => {
    const edit = registryEdits[key]?.[field];
    if (edit !== undefined) return edit as DealTypeRegistryEntry[K];
    const original = registry.find(r => r.deal_type_key === key);
    return original?.[field] as DealTypeRegistryEntry[K];
  };

  // Handle registry row edit
  const handleRegistryEdit = (key: string, field: keyof DealTypeRegistryEntry, value: unknown) => {
    setRegistryEdits(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  // Check if row has unsaved changes
  const hasRegistryChanges = (key: string): boolean => {
    return Object.keys(registryEdits[key] || {}).length > 0;
  };

  // Save registry entry
  const saveRegistryEntry = useCallback(async (dealTypeKey: string) => {
    const rowEdits = registryEdits[dealTypeKey];
    if (!rowEdits || Object.keys(rowEdits).length === 0) return;

    setSavingRegistry(dealTypeKey);
    try {
      const { error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "POST",
        body: {
          _action: "update_registry",
          deal_type_key: dealTypeKey,
          ...rowEdits,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success(`Saved ${dealTypeKey}`);
      setRegistryEdits(prev => {
        const next = { ...prev };
        delete next[dealTypeKey];
        return next;
      });
      onRefresh();
    } catch (err) {
      console.error("[RegistryTablePanel] Save error:", err);
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Failed to save ${dealTypeKey}: ${msg}`);
    } finally {
      setSavingRegistry(null);
    }
  }, [registryEdits, onRefresh]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Deal Type Registry
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-muted/50 animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Deal Type Registry
        </CardTitle>
        <CardDescription>
          Manage deal type enablement and ordering. Theme colors are managed in Theme Labs. Labels and ribbon text are managed in Deal Cards → Components.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-[500px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Key</TableHead>
                <TableHead className="text-center">Enabled</TableHead>
                <TableHead className="text-center">
                  <span className="inline-flex items-center gap-1">
                    <FileImage className="h-3 w-3" />
                    Flyer Evidence
                  </span>
                </TableHead>
                <TableHead className="w-[80px]">Order</TableHead>
                <TableHead className="text-right">Last Updated</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registry.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No deal types found
                  </TableCell>
                </TableRow>
              ) : (
                registry.map((entry) => {
                  const hasChanges = hasRegistryChanges(entry.deal_type_key);
                  const isSaving = savingRegistry === entry.deal_type_key;
                  
                  return (
                    <TableRow key={entry.deal_type_key}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {entry.deal_type_key}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={getRegistryValue(entry.deal_type_key, "is_enabled")}
                          onCheckedChange={(v) => handleRegistryEdit(entry.deal_type_key, "is_enabled", v)}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={getRegistryValue(entry.deal_type_key, "show_flyer_evidence") ?? true}
                          onCheckedChange={(v) => handleRegistryEdit(entry.deal_type_key, "show_flyer_evidence", v)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={getRegistryValue(entry.deal_type_key, "sort_order")}
                          onChange={(e) => handleRegistryEdit(entry.deal_type_key, "sort_order", parseInt(e.target.value, 10))}
                          className="w-16 h-8 text-sm"
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {entry.updated_at ? formatDistanceToNow(new Date(entry.updated_at), { addSuffix: true }) : "-"}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant={hasChanges ? "default" : "ghost"}
                          disabled={!hasChanges || isSaving}
                          onClick={() => saveRegistryEntry(entry.deal_type_key)}
                          className="h-7 px-2"
                        >
                          {isSaving ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

export default RegistryTablePanel;
